import React from 'react';
import { useTranslation } from 'react-i18next';
import { AutoGrowTextarea } from '@/components/ui/auto-grow-textarea';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import type { AnomalyRow } from '@services/activity.service';

const NOTE_MAX_LENGTH = 500;

interface ResolveAnomalyDialogProps {
  target: AnomalyRow | null;
  note: string;
  resolving: boolean;
  onNoteChange: (note: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}

const ResolveAnomalyDialog: React.FC<ResolveAnomalyDialogProps> = ({
  target,
  note,
  resolving,
  onNoteChange,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={target !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="revy-ui w-[calc(100%-2rem)] sm:max-w-[600px]"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="revy-cjk leading-[1.5]">
            {t('anomalies.resolveDialogTitle')}
          </DialogTitle>
        </DialogHeader>

        {target && (
          <div className="mt-2 flex flex-col gap-3">
            <p className="revy-cjk text-sm leading-[1.6]">
              <strong>{t('anomalies.type')}:</strong> {target.event_type}
            </p>
            <p className="revy-cjk text-sm leading-[1.6]">
              <strong>{t('anomalies.severity')}:</strong>{' '}
              {t(`anomalies.severityValues.${target.severity}`)}
            </p>
            <p className="revy-cjk text-sm leading-[1.6]">
              <strong>{t('anomalies.path')}:</strong> {target.request_method}{' '}
              {target.request_path ?? ''}
            </p>
            <Field
              label={t('anomalies.resolveNote')}
              help={`${note.length}/${NOTE_MAX_LENGTH}`}
            >
              {(field) => (
                <AutoGrowTextarea
                  {...field}
                  rows={3}
                  maxLength={NOTE_MAX_LENGTH}
                  value={note}
                  onChange={(event) => onNoteChange(event.target.value)}
                />
              )}
            </Field>
          </div>
        )}

        {/* DialogFooter is flex-col-reverse below sm; MUI's DialogActions stayed
            a row at every width, so the row direction is restored here. */}
        <DialogFooter className="flex-row justify-end gap-2 sm:space-x-0">
          <Button variant="ghost" onClick={onClose} disabled={resolving}>
            <span className="revy-cjk">{t('common.cancel')}</span>
          </Button>
          <Button onClick={onConfirm} disabled={resolving}>
            <span className="revy-cjk">{t('anomalies.markResolved')}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ResolveAnomalyDialog;
